import {
  BRAND_IDENTITY_FACETS,
  SyncCoreDataDefinitionsEnumTranslator,
  TaxonomyCategory,
  TaxonomyFacet,
  TaxonomyTermStatus,
} from '@edgebox/sync-core-data-definitions';
import { ClientTaxonomyEntity } from '@edgebox/sync-core-rest-client';
import { facetIcon as facetIconFromMetadata } from './taxonomyFacetMetadata';

/**
 * What the term list asks the server for.
 *
 * Besides a single status there are two set-valued modes. `'not-archived'` exists because an
 * archived tail is paid for on every load — the list fetches its whole page in one request — and is
 * almost never what the operator came to see.
 */
export type StatusFilter = 'all' | 'not-archived' | TaxonomyTermStatus;
export type TaxonomyRouteMode = 'list' | 'create' | 'detail' | 'edit';

/**
 * Statuses a term list shows when no single status is asked for.
 *
 * `PreviousRevision` is deliberately absent: it is a term's history, not a state a term is in. This
 * is also the single source the status filter's own options are rendered from, so the dropdown can
 * never offer a status the list itself would not honour.
 */
export const LISTED_STATUSES: TaxonomyTermStatus[] = [
  TaxonomyTermStatus.Active,
  TaxonomyTermStatus.Draft,
  TaxonomyTermStatus.Unapproved,
  TaxonomyTermStatus.Archived,
];

/**
 * The list opens without archived terms.
 *
 * Archived terms stay one dropdown entry away — both through `'all'` and through `Archived` on its
 * own — and the option is labelled for the exclusion, so an operator who cannot find a term they
 * archived can see from the control why.
 */
export const DEFAULT_STATUS_FILTER: StatusFilter = 'not-archived';

/** The set-valued filter modes, in the order the dropdown offers them. */
export const STATUS_FILTER_SET_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'not-archived', label: 'All except archived' },
  { value: 'all', label: 'All' },
];

/** The statuses a filter value expands to for the list request. */
export function statusesForFilter(filter: StatusFilter): TaxonomyTermStatus[] {
  if (filter === 'all') {
    return LISTED_STATUSES;
  }

  if (filter === 'not-archived') {
    return LISTED_STATUSES.filter((status) => status !== TaxonomyTermStatus.Archived);
  }

  return [filter];
}

export interface ITaxonomyRouteState {
  mode: TaxonomyRouteMode;
  facet?: TaxonomyFacet;
  /**
   * How the URL named the term: its key where it has one, its id otherwise.
   *
   * The route accepts either, and the parser deliberately does not decide which it was handed —
   * the shape of a key is the server's business, not the router's. Whoever loads the term resolves
   * the reference against both.
   */
  termReference?: string;
}

export const CATEGORY_ORDER: TaxonomyCategory[] = [
  TaxonomyCategory.Strategy,
  TaxonomyCategory.Structure,
  TaxonomyCategory.Audience,
  TaxonomyCategory.System,
  TaxonomyCategory.Content,
  TaxonomyCategory.TrustAndSafety,
  TaxonomyCategory.Operations,
];

export function categoryLabel(category: TaxonomyCategory) {
  if (category === TaxonomyCategory.System) {
    return 'Channels';
  }

  return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaxonomyCategory', category);
}

export function facetIcon(facet?: TaxonomyFacet) {
  return facetIconFromMetadata(facet);
}

export function taxonomyIdentity(taxonomy?: ClientTaxonomyEntity) {
  if (taxonomy?.key) {
    return String(taxonomy.key);
  }
  if (taxonomy?.id) {
    return String(taxonomy.id);
  }
  return taxonomy?.facet ? String(taxonomy.facet) : undefined;
}

export function taxonomyFacetValue(taxonomy?: ClientTaxonomyEntity): TaxonomyFacet | undefined {
  return taxonomy?.facet;
}

export function parseTaxonomyManagementRoute(pathname: string): ITaxonomyRouteState {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'brand-presence' || segments[1] !== 'settings' || segments[2] !== 'taxonomies') {
    return { mode: 'list' };
  }

  const rawFacet = segments[3] ? decodeURIComponent(segments[3]) : undefined;
  const facets = Object.values(TaxonomyFacet) as string[];
  const facet = rawFacet && facets.includes(rawFacet) ? (rawFacet as TaxonomyFacet) : undefined;

  if (segments[4] === 'new') {
    return { mode: 'create', facet };
  }

  if (segments[5] === 'edit' && segments[4]) {
    return { mode: 'edit', facet, termReference: decodeURIComponent(segments[4]) };
  }

  if (segments[4] && !segments[5]) {
    return { mode: 'detail', facet, termReference: decodeURIComponent(segments[4]) };
  }

  return { mode: 'list', facet };
}

export function taxonomyPath(facet?: TaxonomyFacet) {
  return facet ? `/brand-presence/settings/taxonomies/${encodeURIComponent(String(facet))}` : '/brand-presence/settings/taxonomies';
}

/**
 * What another screen already knows about the term it is sending the operator to create.
 *
 * The point of routing through the existing form rather than writing the term directly is that the
 * operator reviews what lands in their taxonomy. Prefilling only saves them the retyping.
 */
export interface ITaxonomyTermCreatePrefill {
  name?: string;
  domain?: string;
}

export function createPath(facet?: TaxonomyFacet, prefill?: ITaxonomyTermCreatePrefill) {
  if (!facet) {
    return '/brand-presence/settings/taxonomies';
  }

  const path = `/brand-presence/settings/taxonomies/${encodeURIComponent(String(facet))}/new`;
  const query = new URLSearchParams();
  if (prefill?.name?.trim()) {
    query.set('name', prefill.name.trim());
  }
  if (prefill?.domain?.trim()) {
    query.set('domain', prefill.domain.trim());
  }

  const serialized = query.toString();
  return serialized ? `${path}?${serialized}` : path;
}

/** Reads back what `createPath` wrote, for the create form to open with. */
export function parseTaxonomyTermCreatePrefill(search: string): ITaxonomyTermCreatePrefill {
  const params = new URLSearchParams((search || '').replace(/^\?/, ''));
  const name = (params.get('name') || '').trim();
  const domain = (params.get('domain') || '').trim();

  return {
    ...(name ? { name } : {}),
    ...(domain ? { domain } : {}),
  };
}

/**
 * The two things a term can be addressed by, as much of them as the calling screen holds.
 *
 * Deliberately `unknown` rather than `string`: the client entity's id is a branded type nothing
 * outside the kit can construct, and a purpose tab carries its own already-stringified pair. Both
 * satisfy this without a cast, and the values are only ever read as strings here.
 */
export interface ITermRouteIdentity {
  id?: unknown;
  key?: unknown;
}

/**
 * What a link to a term's page puts in the URL: the term's key when it has one, its id otherwise.
 *
 * The key leads because it is stable across a term's revisions and readable in a link someone
 * pastes into a message. It is also optional today — only a WriteAll caller may set one, so a term
 * a customer user created carries none — which is what the id fallback is for. Once every term is
 * given a key at creation the fallback has nothing left to answer for and can go.
 */
export function termRouteReference(term: ITermRouteIdentity | undefined): string | undefined {
  const key = term?.key == null ? '' : String(term.key).trim();
  if (key) {
    return key;
  }

  const id = term?.id == null ? '' : String(term.id).trim();
  return id || undefined;
}

/**
 * The tag's own page: what the tag is, at a URL every other screen can link to.
 *
 * Screens that reference a tag — the list row, the chips on analytics screens — send the reader
 * here; `editPath` is reserved for the explicit Edit action. The reference is whatever
 * `termRouteReference` chose for that term, so the same builder serves both key and id URLs.
 */
export function termDetailPath(facet: TaxonomyFacet | undefined, termReference: string | undefined) {
  if (!facet || !termReference) {
    return '/brand-presence/settings/taxonomies';
  }
  return `/brand-presence/settings/taxonomies/${encodeURIComponent(String(facet))}/${encodeURIComponent(termReference)}`;
}

export function editPath(facet: TaxonomyFacet | undefined, termReference: string | undefined) {
  if (!facet || !termReference) {
    return '/brand-presence/settings/taxonomies';
  }
  return `/brand-presence/settings/taxonomies/${encodeURIComponent(String(facet))}/${encodeURIComponent(termReference)}/edit`;
}

/**
 * Whether a tag on this facet must carry a main domain.
 *
 * An authority source stands for one or more hosts, so its domain is mandatory. Every other facet
 * may carry a domain but does not have to. This is the single required-domain predicate: the form's
 * gate and the write path both read it, so what the form demands and what the submit enforces can
 * never drift apart.
 */
export function requiresDomainForFacet(facet?: TaxonomyFacet): boolean {
  return facet === TaxonomyFacet.AuthoritySource;
}

/**
 * Whether a tag on this facet carries a brand identity — a main domain and a brand colour.
 *
 * Driven by the one exported facet list so it widens wherever that list does, and kept apart from
 * the required-domain rule above: these facets may carry a domain, but only the authority source
 * must.
 */
export function carriesBrandIdentity(facet?: TaxonomyFacet): boolean {
  return !!facet && BRAND_IDENTITY_FACETS.includes(facet);
}

export function allowTermCreation(taxonomy?: ClientTaxonomyEntity, facet?: TaxonomyFacet) {
  if (facet === TaxonomyFacet.Locale) {
    return true;
  }

  return !!taxonomy?.allowAddingTerms;
}
